import axios from "axios";

// Advertising System — /api/advertising.
//
// Every price, impression range and duration shown anywhere comes from the
// server's ad-type config (GET /config), never from a constant here, so an
// admin change applies everywhere without a code change.

export { loadRazorpayScript } from "./boostApi";

export const BASE_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// Shown wherever an impression range is shown — and always before payment. A
// range is an estimate, never a promise.
export const ESTIMATE_DISCLAIMER =
  "Estimated impressions are not guaranteed. Actual delivery may vary based on available audience, user activity, targeting, campaign inventory and other platform conditions.";

// How a campaign ends. `days` is the ad type's maximum duration from the
// server config (admin-controlled), never a constant.
export const completionNote = (days) =>
  `The campaign may complete before ${days} days if the minimum estimated delivery target is reached. If the minimum target is not reached earlier, the campaign will end when the ${days}-day campaign period ends.`;

// "https://shop.example.com/path?utm=x" → "shop.example.com". What the
// external-site warning shows: the real destination, without its path or
// query parameters.
export function destinationHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function authConfig() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

const api = (path) => `${BASE_URL}/api/advertising${path}`;

// "1500" → "1,500"
export const formatCount = (n) => Number(n || 0).toLocaleString("en-IN");

export async function getAdConfig() {
  const res = await axios.get(api("/config"));
  return res.data;
}

// ── advertiser ──
export async function createAdCampaign(formData) {
  const res = await axios.post(api("/campaigns"), formData, authConfig());
  return res.data;
}

export async function getMyAdCampaigns() {
  const res = await axios.get(api("/campaigns/mine"), authConfig());
  return res.data;
}

// Draft → security check → Pending Review.
export async function submitAdCampaign(campaignId) {
  const res = await axios.post(api(`/campaigns/${campaignId}/submit`), {}, authConfig());
  return res.data;
}

// Opens a Razorpay order (approved campaigns only). Nothing is paid until the
// SERVER verifies the payment.
export async function createAdOrder(campaignId) {
  const res = await axios.post(api(`/campaigns/${campaignId}/order`), {}, authConfig());
  return res.data;
}

export async function verifyAdPayment(campaignId, payload) {
  const res = await axios.post(api(`/campaigns/${campaignId}/verify`), payload, authConfig());
  return res.data;
}

// ── approximate location (city level) ──
// Used ONLY when the browser has ALREADY granted location permission — the
// feed never prompts. One low-accuracy reading per page load (no
// watchPosition, no background tracking), rounded to 0.1° (~11 km) before it
// leaves the device. The server maps it to a city name for that request and
// stores nothing. Without it the server falls back to an approximate IP city,
// then to no city.
const COARSE = (v) => Math.round(v * 10) / 10;
const POSITION_OPTIONS = { enableHighAccuracy: false, maximumAge: 60 * 60 * 1000, timeout: 5000 };
let approxLocationPromise = null;

function readCoarsePosition() {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: COARSE(pos.coords.latitude), lng: COARSE(pos.coords.longitude) }),
      () => resolve(null),
      POSITION_OPTIONS
    );
  });
}

export function getApproxLocation() {
  if (!approxLocationPromise) {
    approxLocationPromise = (async () => {
      try {
        if (typeof navigator === "undefined" || !navigator.geolocation || !navigator.permissions?.query) return null;
        const status = await navigator.permissions.query({ name: "geolocation" });
        if (status.state !== "granted") return null;
        return await readCoarsePosition();
      } catch {
        return null;
      }
    })();
  }
  return approxLocationPromise;
}

// Settings → Ads: the one place that may show the browser's permission
// prompt, and only when the user presses the button. Resolves true when
// a position could be read.
export async function requestApproxLocation() {
  if (typeof navigator === "undefined" || !navigator.geolocation) return false;
  const pos = await readCoarsePosition();
  approxLocationPromise = Promise.resolve(pos);
  return Boolean(pos);
}

// ── feed ──
// slots: [{ slot, blogIds }]. Resolves to [{ slot, ad, token }].
export async function serveFeedAds(slots, exclude = []) {
  const approxLocation = await getApproxLocation();
  const res = await axios.post(
    api("/serve"),
    { slots, exclude, ...(approxLocation ? { approxLocation } : {}) },
    authConfig()
  );
  return res.data?.ads || [];
}

// One report per served token per page load: a re-render or remount of the
// same ad sends nothing. The server is the real guard (it counts one
// impression per user per campaign per frequency window).
const reportedTokens = new Set();

export async function reportAdImpression(token) {
  if (!token || reportedTokens.has(token)) return;
  reportedTokens.add(token);
  try {
    await axios.post(api("/impression"), { token }, authConfig());
  } catch {
    // Network failure: let the next visibility retry.
    reportedTokens.delete(token);
  }
}

// CTA clicks and video milestones: one report per (served token, event) per
// page load. The server records each at most once per displayed ad, so a
// duplicate listener or re-render can never inflate them.
const reportedEvents = new Set();

async function reportOnce(key, path, body) {
  if (reportedEvents.has(key)) return;
  reportedEvents.add(key);
  try {
    await axios.post(api(path), body, authConfig());
  } catch {
    reportedEvents.delete(key);
  }
}

export function reportAdClick(token) {
  if (!token) return Promise.resolve();
  return reportOnce(`click:${token}`, "/click", { token });
}

// event: "start" | "view" | "complete". Sent in order per ad, because the
// server only accepts a view after a start and a completion after a view.
const videoChains = new Map();

export function reportAdVideoEvent(token, event) {
  if (!token) return Promise.resolve();
  const next = (videoChains.get(token) || Promise.resolve()).then(() =>
    reportOnce(`video:${event}:${token}`, "/video-event", { token, event })
  );
  videoChains.set(token, next);
  return next;
}

// Report a served ad. reason: misleading | suspicious_website | inappropriate
// | not_relevant | other (details required for "other"). The server
// validates everything; this only sends it.
export async function reportAd(token, reason, details = "") {
  const res = await axios.post(api("/report"), { token, reason, details }, authConfig());
  return res.data;
}

export const AD_REPORT_REASONS = [
  { value: "misleading", label: "Misleading" },
  { value: "suspicious_website", label: "Suspicious website" },
  { value: "inappropriate", label: "Inappropriate" },
  { value: "not_relevant", label: "Not relevant" },
  { value: "other", label: "Other" },
];

// ── Settings → Ads ──
export async function getAdPreferences() {
  const res = await axios.get(api("/preferences"), authConfig());
  return res.data;
}

export async function saveAdPreferences({ personalizedAds }) {
  const res = await axios.put(api("/preferences"), { personalizedAds }, authConfig());
  return res.data;
}

// ── admin ──
export const adminAds = {
  listCampaigns: (status) =>
    axios
      .get(api("/admin/campaigns"), { ...authConfig(), params: status ? { status } : {} })
      .then((r) => r.data),
  getCampaign: (id) => axios.get(api(`/admin/campaigns/${id}`), authConfig()).then((r) => r.data),
  stats: () => axios.get(api("/admin/stats"), authConfig()).then((r) => r.data),
  approve: (id, { acknowledgeSecurity } = {}) =>
    axios
      .post(api(`/admin/campaigns/${id}/approve`), acknowledgeSecurity ? { acknowledgeSecurity: true } : {}, authConfig())
      .then((r) => r.data),
  reject: (id, reason) =>
    axios.post(api(`/admin/campaigns/${id}/reject`), { reason }, authConfig()).then((r) => r.data),
  pause: (id, reason) =>
    axios.post(api(`/admin/campaigns/${id}/pause`), reason ? { reason } : {}, authConfig()).then((r) => r.data),
  resume: (id) => axios.post(api(`/admin/campaigns/${id}/resume`), {}, authConfig()).then((r) => r.data),
  remove: (id, reason) =>
    axios.post(api(`/admin/campaigns/${id}/remove`), reason ? { reason } : {}, authConfig()).then((r) => r.data),
  recheckSecurity: (id) =>
    axios.post(api(`/admin/campaigns/${id}/security-check`), {}, authConfig()).then((r) => r.data),
  reports: (status = "open") =>
    axios.get(api("/admin/reports"), { ...authConfig(), params: { status } }).then((r) => r.data),
  reviewReports: (campaignId, resolution) =>
    axios.post(api(`/admin/reports/${campaignId}/review`), { resolution }, authConfig()).then((r) => r.data),
  getTypes: () => axios.get(api("/admin/ad-types"), authConfig()).then((r) => r.data),
  updateType: (type, patch) =>
    axios.put(api(`/admin/ad-types/${type}`), patch, authConfig()).then((r) => r.data),
  // Ads the admin posts directly (no review, no payment) → Read Blogs.
  listPosted: () => axios.get(api("/admin/admin-ads"), authConfig()).then((r) => r.data),
  // Registered members per targeting choice: { city, language, category } ("" = not chosen).
  audience: ({ city = "", language = "", category = "" } = {}) =>
    axios
      .get(api("/admin/admin-ads/audience"), { ...authConfig(), params: { city, language, category } })
      .then((r) => r.data),
  createPosted: (formData) => axios.post(api("/admin/admin-ads"), formData, authConfig()).then((r) => r.data),
  setPostedStatus: (id, status) =>
    axios.post(api(`/admin/admin-ads/${id}/status`), { status }, authConfig()).then((r) => r.data),
  deletePosted: (id) => axios.delete(api(`/admin/admin-ads/${id}`), authConfig()).then((r) => r.data),
};
